import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import api from '../api';

const importanceConfig = {
  all:    { label: 'All Alerts',  color: '#3F72AF', bg: '#3F72AF18' },
  info:   { label: 'Info',        color: '#2563EB', bg: '#2563EB15' },
  low:    { label: 'Low',         color: '#16a34a', bg: '#16a34a15' },
  medium: { label: 'Medium',      color: '#d97706', bg: '#d9770615' },
  high:   { label: 'Urgent',      color: '#dc2626', bg: '#dc262615' }
};

const importanceIcons = {
  info: '🔵', low: '🟢', medium: '🟠', high: '🔴'
};

function AlertCard({ alert }) {
  const [expanded, setExpanded] = useState(false);
  const imp    = alert.importance || 'info';
  const config = importanceConfig[imp] || importanceConfig.info;
  const isLong = alert.message && alert.message.length > 120;
  const displayMsg = !expanded && isLong
    ? alert.message.slice(0, 120).trim() + '...'
    : alert.message;

  const date = new Date(alert.createdAt).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric'
  });

  return (
    <div style={{
      background: '#fff',
      borderRadius: 14,
      overflow: 'hidden',
      boxShadow: '0 4px 20px rgba(17,45,78,0.07)',
      transition: 'transform 0.25s ease, box-shadow 0.25s ease'
    }}
      onMouseEnter={function(e) {
        e.currentTarget.style.transform = 'translateY(-3px)';
        e.currentTarget.style.boxShadow = '0 12px 32px rgba(17,45,78,0.13)';
      }}
      onMouseLeave={function(e) {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 4px 20px rgba(17,45,78,0.07)';
      }}
    >
      {/* Top color band */}
      <div style={{ height: 4, background: config.color }} />

      <div style={{ padding: '20px 22px 22px' }}>
        {/* Header row */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: '1.2rem', flexShrink: 0 }}>{importanceIcons[imp]}</span>
            <span style={{
              background: config.bg, color: config.color,
              fontSize: '0.68rem', fontWeight: 700,
              letterSpacing: '0.1em', textTransform: 'uppercase',
              padding: '3px 10px', borderRadius: 20
            }}>
              {importanceConfig[imp]?.label || 'Info'}
            </span>
          </div>
          <span style={{ fontSize: '0.75rem', opacity: 0.45, flexShrink: 0, marginTop: 2 }}>{date}</span>
        </div>

        {/* Title */}
        <h3 style={{
          margin: '0 0 10px', fontSize: '1rem',
          color: 'var(--bj-espresso)', lineHeight: 1.35
        }}>
          {alert.title}
        </h3>

        {/* Message */}
        <p style={{
          margin: 0, fontSize: '0.9rem',
          color: 'var(--bj-espresso)', opacity: 0.72,
          lineHeight: 1.72
        }}>
          {displayMsg}
        </p>

        {/* Read more */}
        {isLong && (
          <button
            onClick={function() { setExpanded(!expanded); }}
            style={{
              background: 'none', border: 'none', padding: 0,
              marginTop: 10, cursor: 'pointer',
              fontFamily: 'var(--font-body)', fontWeight: 700,
              fontSize: '0.83rem', color: config.color,
              display: 'flex', alignItems: 'center', gap: 5
            }}
          >
            {expanded ? 'Show less ↑' : 'Read more ↓'}
          </button>
        )}
      </div>
    </div>
  );
}

export default function TravelAlerts() {
  const [alerts, setAlerts]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [filter, setFilter]       = useState('all');

  useEffect(function() {
    api.get('/alerts?type=travel-alert')
      .then(function(res) {
        var sorted = res.data.sort(function(a, b) {
          // Sort by importance severity first, then by date
          var order = { high: 0, medium: 1, low: 2, info: 3 };
          var diff = (order[a.importance] || 3) - (order[b.importance] || 3);
          if (diff !== 0) return diff;
          return new Date(b.createdAt) - new Date(a.createdAt);
        });
        setAlerts(sorted);
      })
      .catch(function() {})
      .finally(function() { setLoading(false); });
  }, []);

  var filtered = filter === 'all'
    ? alerts
    : alerts.filter(function(a) { return a.importance === filter; });

  var counts = {};
  ['info', 'low', 'medium', 'high'].forEach(function(imp) {
    counts[imp] = alerts.filter(function(a) { return a.importance === imp; }).length;
  });

  var urgentCount = alerts.filter(function(a) { return a.importance === 'high'; }).length;

  return (
    <Layout>
      {/* HERO BAND */}
      <div style={{
        background: 'linear-gradient(135deg, var(--bj-espresso) 0%, #1e3f6b 100%)',
        padding: '64px 0 56px', position: 'relative', overflow: 'hidden'
      }}>
        <div style={{ position: 'absolute', top: -60, right: -60, width: 260, height: 260, borderRadius: '50%', background: 'rgba(63,114,175,0.1)', pointerEvents: 'none' }} />
        <div className="container" style={{ position: 'relative' }}>
          <span className="eyebrow" style={{ color: 'var(--bj-sand)' }}>Visitor Resources</span>
          <h1 style={{ color: '#fff', margin: '10px 0 14px', fontSize: 'clamp(1.8rem, 4vw, 2.8rem)' }}>
            Travel &amp; Visa Alerts
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.65)', maxWidth: 520, lineHeight: 1.75, margin: 0 }}>
            Stay current on entry requirements, visa rule changes, and travel advisories
            across every destination we serve. Updated by our team in real time.
          </p>

          {/* Summary pills */}
          {!loading && alerts.length > 0 && (
            <div style={{ display: 'flex', gap: 10, marginTop: 24, flexWrap: 'wrap' }}>
              <div style={{
                background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: 20, padding: '6px 14px',
                fontSize: '0.8rem', color: '#fff', fontWeight: 600
              }}>
                {alerts.length} active alert{alerts.length !== 1 ? 's' : ''}
              </div>
              {urgentCount > 0 && (
                <div style={{
                  background: 'rgba(220,38,38,0.25)', backdropFilter: 'blur(8px)',
                  border: '1px solid rgba(220,38,38,0.4)',
                  borderRadius: 20, padding: '6px 14px',
                  fontSize: '0.8rem', color: '#fca5a5', fontWeight: 700
                }}>
                  🔴 {urgentCount} urgent
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <section className="section">
        <div className="container">

          {/* FILTER BAR */}
          <div style={{
            display: 'flex', gap: 8, flexWrap: 'wrap',
            marginBottom: 32, alignItems: 'center'
          }}>
            <span style={{ fontSize: '0.82rem', fontWeight: 700, opacity: 0.5, marginRight: 4 }}>
              Filter:
            </span>
            {Object.keys(importanceConfig).map(function(key) {
              var isActive = filter === key;
              var conf     = importanceConfig[key];
              var count    = key === 'all' ? alerts.length : (counts[key] || 0);
              return (
                <button
                  key={key}
                  onClick={function() { setFilter(key); }}
                  style={{
                    padding: '7px 16px',
                    borderRadius: 20,
                    border: '1.5px solid ' + (isActive ? conf.color : 'var(--bj-sand)'),
                    background: isActive ? conf.bg : 'transparent',
                    color: isActive ? conf.color : 'var(--bj-espresso)',
                    fontFamily: 'var(--font-body)',
                    fontWeight: 700, fontSize: '0.82rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex', alignItems: 'center', gap: 6
                  }}
                >
                  {key !== 'all' && <span style={{ fontSize: '0.7rem' }}>{importanceIcons[key]}</span>}
                  {conf.label}
                  <span style={{
                    background: isActive ? conf.color : 'var(--bj-sand)',
                    color: isActive ? '#fff' : 'var(--bj-espresso)',
                    fontSize: '0.68rem', fontWeight: 800,
                    padding: '1px 7px', borderRadius: 20,
                    transition: 'all 0.2s'
                  }}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* ALERTS GRID */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '48px 0', opacity: 0.5 }}>
              Loading alerts...
            </div>
          ) : filtered.length === 0 ? (
            <div style={{
              textAlign: 'center', padding: '56px 24px',
              background: '#fff', borderRadius: 14,
              boxShadow: '0 4px 20px rgba(17,45,78,0.06)'
            }}>
              <span style={{ fontSize: '2rem', display: 'block', marginBottom: 12 }}>✅</span>
              <h3 style={{ margin: '0 0 8px' }}>No alerts in this category</h3>
              <p style={{ opacity: 0.6, margin: 0 }}>
                {filter === 'all'
                  ? 'No active travel alerts at this time. All clear!'
                  : 'No ' + importanceConfig[filter].label.toLowerCase() + ' alerts currently active.'}
              </p>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: 20
            }}>
              {filtered.map(function(alert) {
                return <AlertCard key={alert._id} alert={alert} />;
              })}
            </div>
          )}

          {/* Last updated note */}
          {!loading && alerts.length > 0 && (
            <p style={{
              textAlign: 'center', marginTop: 36,
              fontSize: '0.8rem', opacity: 0.45
            }}>
              Alerts are updated in real time by the Bluejay Travels team.
              Last entry: {new Date(alerts[0]?.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}.
            </p>
          )}
        </div>
      </section>
    </Layout>
  );
}